import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/pro-solid-svg-icons";
import JourneyListingUserDetailDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingUserDetailDto";
import JourneyListingDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import cx from "classnames";
import Avatar from "../../../common/Avatar";
import dateHelper from "../../../../helpers/dateHelper";
import PaginationControls from "../../../common/PaginationControls";
import { JourneyFormButtonArg } from "../../../../types/dashboard/JourneyFormDropDownItems";
import JourneyTableActionButtons from "./JourneyTableActionButtons";
import { v4 as uuidv4 } from "uuid";
import listingHelper from "../../../../helpers/listingHelper";

interface JourneyListTableViewProps {
  users: JourneyListingUserDetailDto[] | null;
  journeys: JourneyListingDto[] | null;
  hasPreventJourneySwitchConfigEnabled: boolean;
  onUpdateClick: (journey: JourneyListingDto) => void;
  onViewClick: (journey: JourneyListingDto) => void;
  onDualPrepItemClick: (
    user: JourneyListingUserDetailDto,
    journey: JourneyListingDto,
    arg: JourneyFormButtonArg
  ) => void;
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
}

// Define sort column types
type SortColumn = "NAME" | "JOURNEY" | "DATE_SENT" | "LAST_UPDATED" | "STATUS";
type SortDirection = "ASC" | "DESC";

const JourneyListTableView: React.FC<JourneyListTableViewProps> = ({
  users,
  journeys,
  hasPreventJourneySwitchConfigEnabled,
  onUpdateClick,
  onViewClick,
  onDualPrepItemClick,
  pageNumber,
  setPageNumber,
}) => {
  const { t } = useTranslation();
  const [sortColumn, setSortColumn] = useState<SortColumn>("STATUS");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ASC");
  const [pageSize, setPageSize] = useState(10);

  const dropdownPortalMenuId =
    "journey-action-portal_" + uuidv4().replaceAll("-", "");
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });

  // Define custom status sort order - prioritize journeys that need action
  const getStatusSortOrder = useCallback((status: string): number => {
    switch (status) {
      case "NOT-STARTED":
        return 1; // Highest priority - needs to be started
      case "IN-PROGRESS":
        return 2; // Second priority - currently being worked on
      case "UPCOMING":
        return 3; // Third priority - scheduled for future
      case "COMPLETED":
        return 4; // Lowest priority - already done
      default:
        return 999; // Unknown statuses go to the end
    }
  }, []);

  // Get user by id
  const getUserById = useCallback(
    (userId: number): JourneyListingUserDetailDto | undefined => {
      return users?.find((user) => user.employeeId === userId);
    },
    [users]
  );

  // Handle sort column change
  const handleSortColumnChange = useCallback(
    (column: SortColumn) => {
      let newDirection: SortDirection = "ASC";

      if (column === sortColumn) {
        // Toggle direction if same column
        newDirection = sortDirection === "ASC" ? "DESC" : "ASC";
      }

      setSortColumn(column);
      setSortDirection(newDirection);
      // Reset to first page when sorting changes
      setPageNumber(1);
    },
    [sortColumn, sortDirection, setPageNumber]
  );

  // Get sort icon
  const getSortIcon = useCallback(
    (column: SortColumn) => {
      return (
        <>
          {column !== sortColumn && (
            <FontAwesomeIcon icon={faSort} className="text-gray-300 pt-1" />
          )}
          {column === sortColumn && sortDirection === "ASC" && (
            <FontAwesomeIcon icon={faSortUp} className="text-gray-500 pt-1" />
          )}
          {column === sortColumn && sortDirection === "DESC" && (
            <FontAwesomeIcon icon={faSortDown} className="text-gray-500 pt-1" />
          )}
        </>
      );
    },
    [sortColumn, sortDirection]
  );

  // Sort journeys with improved stability using unique ID as tiebreaker
  const sortedJourneys = React.useMemo(() => {
    if (!journeys) return [];

    // We need to prevent modifying or recreating journey objects when sorting
    // This ensures React treats each journey as a unique and stable entity
    return [...journeys].sort((a, b) => {
      const multiplier = sortDirection === "ASC" ? 1 : -1;
      let compareResult = 0;

      switch (sortColumn) {
        case "NAME": {
          const userA = getUserById(a.subjectEmployeeId);
          const userB = getUserById(b.subjectEmployeeId);
          const nameA = userA?.employeeFullName || "";
          const nameB = userB?.employeeFullName || "";
          compareResult = nameA.localeCompare(nameB);

          // If names are equal, sort by last updated date (most recent first)
          if (compareResult === 0) {
            const dateA = a.dateLastUpdated
              ? new Date(a.dateLastUpdated).getTime()
              : 0;
            const dateB = b.dateLastUpdated
              ? new Date(b.dateLastUpdated).getTime()
              : 0;
            return (dateB - dateA) * multiplier; // Sort most recent first
          }
          break;
        }
        case "JOURNEY":
          compareResult = a.title.localeCompare(b.title);
          break;
        case "DATE_SENT": {
          const dateA = a.dateSent ? new Date(a.dateSent).getTime() : 0;
          const dateB = b.dateSent ? new Date(b.dateSent).getTime() : 0;
          compareResult = dateA - dateB;
          break;
        }
        case "LAST_UPDATED": {
          const dateA = a.dateLastUpdated
            ? new Date(a.dateLastUpdated).getTime()
            : 0;
          const dateB = b.dateLastUpdated
            ? new Date(b.dateLastUpdated).getTime()
            : 0;
          compareResult = dateA - dateB;
          break;
        }
        case "STATUS": {
          // Use custom status sort order instead of alphabetical
          const statusOrderA = getStatusSortOrder(a.overallStatus);
          const statusOrderB = getStatusSortOrder(b.overallStatus);
          compareResult = statusOrderA - statusOrderB;
          break;
        }
      }

      // Apply the multiplier for ASC/DESC direction
      compareResult *= multiplier;

      // If primary sort criteria are equal, use consistent secondary criteria
      if (compareResult === 0) {
        // First try to differentiate by employee ID
        if (a.subjectEmployeeId !== b.subjectEmployeeId) {
          return a.subjectEmployeeId - b.subjectEmployeeId;
        }
        // Then by journey title
        const titleCompare = a.title.localeCompare(b.title);
        if (titleCompare !== 0) {
          return titleCompare;
        }
        // Finally by journey ID to ensure total stability
        return a.id - b.id;
      }

      return compareResult;
    });
  }, [journeys, sortColumn, sortDirection, getUserById]);

  // Paginate journeys - ensure we're dealing with an array of unique items
  const paginatedJourneys = React.useMemo(() => {
    // Create a map to ensure we only include each journey once
    // Using a composite key for journeys that might have the same ID (like Upcoming, or Not Started,
    // journeys with ID 0)
    const uniqueJourneys = new Map();

    // Generate a more comprehensive unique key for each journey
    sortedJourneys.forEach((journey) => {
      // For upcoming journeys that might have ID 0, create a composite key
      const compositeKey =
        (journey.overallStatus === "UPCOMING" ||
          journey.overallStatus === "NOT-STARTED") &&
        journey.id === 0
          ? `${journey.id}-${journey.subjectEmployeeId}-${journey.title}`
          : journey.id.toString();

      if (!uniqueJourneys.has(compositeKey)) {
        uniqueJourneys.set(compositeKey, journey);
      }
    });

    // Convert map values back to array for pagination
    const dedupedJourneys = Array.from(uniqueJourneys.values());

    // Apply pagination
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return dedupedJourneys.slice(startIndex, endIndex);
  }, [sortedJourneys, pageNumber, pageSize]);

  // Format date
  const formatDate = useCallback((dateString: Date | null | undefined) => {
    if (!dateString) return "";
    return dateHelper
      .convertUtcDateToLocal(dateString)
      .toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
  }, []);

  if (!users || !journeys) {
    return (
      <div className="p-4 text-center text-gray-500">No data available.</div>
    );
  }

  return (
    <div>
      {paginatedJourneys.length === 0 ? (
        <div className="p-4 text-center text-gray-500">No journeys found.</div>
      ) : (
        <>
          <div className="w-full overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer group"
                    onClick={() => handleSortColumnChange("JOURNEY")}
                  >
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap">Journey</span>
                      <span className="ml-auto">{getSortIcon("JOURNEY")}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-900 cursor-pointer group"
                    onClick={() => handleSortColumnChange("NAME")}
                  >
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap">
                        {t("Pages.Admin.Common.Name")}
                      </span>
                      <span className="ml-auto">{getSortIcon("NAME")}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="hidden md:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer group"
                    onClick={() => handleSortColumnChange("DATE_SENT")}
                  >
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap">Date Sent</span>
                      <span className="ml-auto">
                        {getSortIcon("DATE_SENT")}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="hidden md:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer group"
                    onClick={() => handleSortColumnChange("LAST_UPDATED")}
                  >
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap">Last Updated</span>
                      <span className="ml-auto">
                        {getSortIcon("LAST_UPDATED")}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-sm font-semibold text-gray-900 cursor-pointer group"
                    onClick={() => handleSortColumnChange("STATUS")}
                  >
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap flex-1 text-center">
                        Status
                      </span>
                      <span className="ml-auto">{getSortIcon("STATUS")}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-sm font-semibold text-gray-900"
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {paginatedJourneys.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-gray-500">
                      No journeys found.
                    </td>
                  </tr>
                ) : (
                  paginatedJourneys.map((journey) => {
                    const user = getUserById(journey.subjectEmployeeId);

                    // Create a truly unique key for React rendering
                    // For upcoming journeys with ID 0, make sure the key is still unique
                    const uniqueKey =
                      journey.overallStatus === "UPCOMING" && journey.id === 0
                        ? `journey-${journey.id}-${journey.subjectEmployeeId}-${journey.title}`
                        : `journey-${journey.id}-${journey.subjectEmployeeId}`;

                    return (
                      <tr
                        key={uniqueKey}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                          {journey.title}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-6 pr-3 text-sm">
                          <div className="flex items-center">
                            <Avatar
                              imageUrl={user?.profileImageUrl}
                              userFullName={user?.employeeFullName || ""}
                              userInitials={user?.initials || ""}
                              size={8}
                              activeStatus="DO-NOT-SHOW"
                            />
                            <div className="ml-3 font-medium text-gray-900">
                              {user?.employeeFullName || "Unknown"}
                            </div>
                          </div>
                        </td>
                        <td className="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                          {formatDate(journey.dateSent)}
                        </td>
                        <td className="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                          {formatDate(journey.dateLastUpdated)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                          <span
                            className={cx(
                              "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                              listingHelper.getJourneyStatusColor(journey)
                            )}
                          >
                            {listingHelper.getJourneyStatusText(journey)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                          <div className="w-28">
                            <JourneyTableActionButtons
                              journey={journey}
                              user={user}
                              hasPreventJourneySwitchConfigEnabled={
                                hasPreventJourneySwitchConfigEnabled
                              }
                              onUpdateClick={onUpdateClick}
                              onViewClick={onViewClick}
                              onDualPrepItemClick={onDualPrepItemClick}
                              dropdownPortalMenuId={dropdownPortalMenuId}
                              setPortalPosition={setPortalPosition}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            {/* This div acts as the dropdown menu when its exposed. It has been portaled 
                here to avoid being clipped by the max-h-64 and overview set in the div above */}
            <div
              id={dropdownPortalMenuId}
              className="absolute"
              style={{
                top: `${portalPosition.top}px`,
                left: `${portalPosition.left}px`,
              }}
            ></div>
          </div>

          <div className="mt-4 overflow-x-auto -mx-2 sm:mx-0">
            <PaginationControls
              activePage={pageNumber}
              itemsPerPage={pageSize}
              totalItemCount={sortedJourneys.length}
              isLoading={false}
              loadingText={t("Common.Loading") + "..."}
              onPageChange={setPageNumber}
              onPageSizeChange={setPageSize}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default JourneyListTableView;
